import React, { useCallback, useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import classes from './ExpenseCardList.module.scss';
import ThreeDotMenu from '../../../../common/ThreeDotMenu/ThreeDotMenu';
import AddGradientIcon from '../../../../common/Icons/AddGradientIcon';
// eslint-disable-next-line import/no-cycle
import { EditExpenseContext } from '../../Expense';
import { ExpenseColumnType } from '../MainTable/types';
import colors from '../../../../../scss/colors';
import NotificationModal from '../../../../common/ModalWindows/NotificationModal';
import { useWindowSize } from '../../../../../utils/hooks';
import { tneAxiosClient } from '../../../../../api/apiSettings';
import { updateClaim, updateClaimData } from '../../../../../actions/expense';
import { responsiveSizeTimesheet } from '../../../../../utils/constants';
import { PageContext } from '../../../../../context/context';
import { generalEnums } from '../../../../../utils/enums';
// eslint-disable-next-line import/no-cycle
import TimeandExpenseActions from '../../../../common/buttons/TimeandExpenseActions/TimeandExpenseActions';
import ButtonComponent from '../../../../common/buttons/ButtonComponent/ButtonComponent';
import { AddAttachmentIcon } from '../../../../common/Icons';
import { buttonVariants } from '../../../../common/buttons/types';
import BaseModal from '../../../../common/ModalWindows/BaseModal';
import WithCloseIcon from '../../../../common/ComposableWrappers/WithCloseIcon/WithCloseIcon';
import { basicErrorHandler } from '../../../../../utils/helpers';
import {
  Categories,
  Comment,
  Currencies,
  DayPicker,
  ExpenseAmount,
  NoReceiptCheckbox,
  Rate,
  ReceiptUploader,
  SaveandUpdateButton
} from './ExpenseCardListConfig';
import CustomDocViewer from '../../../../common/CustomDocViewer/CustomDocViewer';
import { AttachmentItem } from '../../../timesheet/Attachments/Attachments';
import MainButton from '../../../../common/buttons/MainButton';

const ThreeDotContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  gap: '24px',
  width: '100%'
}));

const textFieldStyles = (width, radius) => ({
  borderRadius: radius,
  width,
  background: colors.WHITE,
  color: colors.BLACK,
  marginBottom: '30px',
  fieldset: {
    border: `1px solid ${colors.LIGHT_GREY}`,
    borderRadius: radius,
    legend: {
      width: 'unset'
    }
  }
});

export default function ExpenseCardList({
  data,
  addHandler,
  changeHandler,
  saveHandler,
  submitHandler,
  totalClaim,
  expenseCurrency,
  deleteLineHandler,
  saving,
  editable,
  getFormData
}) {
  const expensetypes = useSelector((state) => state.constFromApi.expensetypes);
  const currencies = useSelector((state) => state.constFromApi.currencies);
  const isApprover = useSelector((state) => state.accessToParts.tAndEApprover);
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [category, setCategory] = useState('');
  const [showCurrency, setShowCurrency] = useState(null);
  const [fxRate, setFxRate] = useState(1);
  const [currency, setCurrency] = useState('');
  const [fileSet, setFiles] = useState([]);
  const ctx = useContext(EditExpenseContext);
  const pageCtx = useContext(PageContext);
  const size = useWindowSize();
  const dispatch = useDispatch();
  const [openPopUp, setOpenPop] = useState(false);
  const [openEditClaim, setOpenEditClaim] = useState(false);
  const [openSwiper, setOpenSwiper] = useState(false);
  const [checked, setChecked] = useState(false);
  const [attachmentList, setAttachments] = useState([]);
  const [attachmentModal, setAttachmentModal] = useState({
    show: false,
    id: '',
    filename: '',
    data: ''
  });
  const [opening, setOpening] = useState(false);
  const { width } = useWindowSize();
  const [loading, setLoading] = useState(false);
  const [renameError, setRenameError] = useState('');

  const addNewClaim = () => {
    addHandler();
    ctx.setOpenClaimSwiper(true);
  };

  const uploadHandler = async (files, expense) => {
    setFiles(files);
    const formData = new FormData();
    formData.append('attachments', files[0]);
    let newAttachmentsList = [];
    try {
      setLoading(true);
      if (expense?.Type !== 'New') {
        const result = await tneAxiosClient.post(`v1/expense/attachment/${expense?.Id}`, formData);
        newAttachmentsList = result?.data?.Data?.Data;
      } else {
        newAttachmentsList = [...expense.ExpenseAttachments, files[0]];
      }
      const expenseDetail = {
        ...expense,
        ExpenseAttachments: newAttachmentsList,
        attachments: newAttachmentsList
      };
      dispatch(updateClaim(expenseDetail));
      ctx.setHasChanges(true);
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
      setLoading(false);
    }
    setLoading(false);
  };

  const deleteHandler = async (id, expense) => {
    const attachments = expense?.ExpenseAttachments?.filter((file) => file.Id !== id);
    if (expense?.Id) {
      await tneAxiosClient.delete(`v1/expense/attachment/${id}`);
    }
    const expenseDetail = {
      ...expense,
      ExpenseAttachments: attachments,
      attachments
    };
    dispatch(updateClaim(expenseDetail));

    if (!expense?.ExpenseAttachments.length) {
      ctx.setHasChanges(false);
    }
    ctx.setHasChanges(true);
  };

  const setCategoryType = (cat, rowId) => {
    setCategory(cat);
    changeHandler(ExpenseColumnType.CATEGORY, rowId, cat.value);
  };

  useEffect(() => {
    pageCtx.setPageType(generalEnums.EXPENSE);
  }, [pageCtx]);

  useEffect(() => {
    if (ctx.openClaimSwiper) {
      setOpenSwiper(true);
    } else {
      setOpenSwiper(false);
    }
  }, [ctx.openClaimSwiper]);

  const handleChecked = (e, expense) => {
    setChecked(e.target.checked);
    if (e.target.checked) {
      const expenseDetail = {
        ...expense,
        ExpenseAttachments: [],
        attachments: []
      };
      dispatch(updateClaim(expenseDetail));
      ctx.setHasChanges(true);
    }

    if (!e.target.checked && !expense?.ExpenseAttachments.length) {
      ctx.setHasChanges(false);
    }
  };

  const handleAttachments = () => {
    setOpenPop(true);
  };

  const onCardOpen = (expense) => {
    try {
      if (expense?.ExpenseAttachments?.length) {
        expense?.ExpenseAttachments?.forEach(async (item) => {
          const result = await tneAxiosClient.get(`v1/expense/download-attachment?expenseAttachmentId=${item?.Id}`);
          setAttachments((prevState) => [
            ...prevState,
            {
              uri: result?.data?.Data
            }
          ]);
        });
      }
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
    }
  };

  const closeAttachmentViewer = () => {
    setOpenPop(false);
    setAttachments([]);
  };

  const handleAttachmentViewer = async (file) => {
    try {
      if (file?.Id) {
        setOpening(true);
        const result = await tneAxiosClient.get(`v1/expense/download-attachment?expenseAttachmentId=${file?.Id}`);
        if (result.status === 200) {
          setAttachmentModal({
            show: true,
            id: file?.Id,
            filename: file?.FileAttachmentName,
            data: result?.data?.Data
          });
          setOpening(false);
        }
      }
    } catch (e) {
      setOpening(false);
      basicErrorHandler(dispatch, e.message);
    }
  };

  const handleDownload = useCallback(() => {
    const link = document.createElement('a');
    link.href = attachmentModal.data;
    link.download = attachmentModal.filename;
    link.click();
  }, [attachmentModal.filename]);

  const handleClaimDescription = async () => {
    const formData = getFormData(data);

    if (data?.ClaimDescription.trim().length === 0) {
      setRenameError('Please mention claim description');
      return;
    }
    setRenameError('');
    formData.append('ClaimDescription', data.ClaimDescription);
    await tneAxiosClient.put(`v1/expense?expenseId=${data.Id}`, formData);
    setOpenEditClaim(false);
  };

  const handleClaimDescriptionChange = (e) => {
    if (e.target.value.trim() === 0) return;
    setRenameError('');
    dispatch(updateClaimData('ClaimDescription', e.target.value));
  };

  const handleCloseRenameClaim = () => {
    if (data?.ClaimDescription.trim().length === 0) {
      setRenameError('Please mention claim description');
      return;
    }

    setOpenEditClaim(false);
  };

  return (
    <div className={classes.list}>
      <div className={classes.clientWrapper}>
        <p role="presentation" onClick={() => setOpenEditClaim(true)} className={classes.client}>
          {data?.ClaimDescription}
        </p>
        <BaseModal
          open={openEditClaim}
          closeHandler={() => handleCloseRenameClaim()}
          title="&nbsp;"
          hideHeader
          hideFooter
          withSeparator={false}
          marginLeftRight={1}
          dialogContentStyle={{ overflowY: 'auto' }}
          paperWidth="100%"
          backgroundColor={colors.PAGE_BACKGROUND}
        >
          <WithCloseIcon close={() => handleCloseRenameClaim()}>
            <div className={classes.modalWrapper}>
              <h5 className={classes.renameDoc}>{t('dashboard.expenses.renamedocument')}</h5>
              <TextField
                value={data?.ClaimDescription}
                onChange={(e) => handleClaimDescriptionChange(e)}
                sx={textFieldStyles('100%', '10px')}
                inputProps={{ maxLength: 50 }}
              />
              {renameError && <p>{renameError}</p>}
              <MainButton
                size="small"
                variant="primary"
                rounded
                action={() => handleClaimDescription()}
                forceWidth={size.width > responsiveSizeTimesheet ? 'fit-content' : '100%'}
              >
                {t('common.general.ok')}
              </MainButton>
            </div>
          </WithCloseIcon>
        </BaseModal>
      </div>
      {data?.ExpenseDetails?.map((expense) => (
        <ThreeDotMenu
          key={expense.Id}
          onOpen={() => onCardOpen(expense)}
          onClose={() => setAttachments([])}
          imperativeState={openPopUp}
          autoTrigger={openSwiper}
          verticalScroll="hidden"
          menuComponent={
            <div
              role="presentation"
              key={expense.Id}
              className={`${
                expense?.ExpenseAttachments?.length > 0 ? classes.card : `${classes.card} ${classes.noAttachment}`
              }`}
            >
              <div className={classes.doubleText}>
                <p className={classes.location}>{expense?.Category?.Name}</p>
                <p className={classes.worktype}>{dayjs(expense?.Date).format('DD MMM YYYY')}</p>
              </div>
              <div className={classes.hoursWrapper}>
                <p className={classes.hours}>
                  {expense?.ExpenseCurrency?.CurrencyName} {expense?.TotalAmount}
                </p>
              </div>
              <div className={classes.arrow}>
                <ArrowForwardIosIcon sx={{ opacity: 0.1 }} />
              </div>
              <div className={classes.cornerCount}>{expense?.ExpenseAttachments?.length || 0}</div>
            </div>
          }
        >
          {editable ? (
            <ThreeDotContainer>
              <div className={classes.mobileItemWrapper}>
                <Categories
                  t={t}
                  classes={classes}
                  category={category}
                  expense={expense}
                  action={(types, id) => setCategoryType(types, id)}
                  expensestypes={expensetypes}
                />
                <div className={classes.mobileItemWrapperSection}>
                  <h5 className={classes.title}>
                    {expense?.CategoryId === 5 || category.value === 5
                      ? t('dashboard.expenses.table.mileage')
                      : t('dashboard.expenses.table.expenseamount')}
                    *
                  </h5>
                  <div className={classes.content}>
                    {expense?.CategoryId === 5 || category.value === 5 ? (
                      <>
                        <Rate
                          t={t}
                          expense={expense}
                          action={(e) => {
                            setFxRate(e.target.value);
                            changeHandler(ExpenseColumnType.RATE, expense.Id, e.target.value);
                          }}
                          value={expense?.FxAmount || null}
                          disability
                          radius="10px 0px 0px 10px"
                        />
                        <ExpenseAmount
                          t={t}
                          expense={expense}
                          category={category}
                          value={expense?.ExpenseAmount || null}
                          radius="0px 10px 10px 0px"
                          action={(e) => {
                            setFxRate(e.target.value);
                            changeHandler(ExpenseColumnType.EXPENSE_AMOUNT, expense.Id, e.target.value);
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <ExpenseAmount
                          t={t}
                          expense={expense}
                          category={category}
                          value={expense?.ExpenseAmount || null}
                          action={(e) => {
                            changeHandler(ExpenseColumnType.EXPENSE_AMOUNT, expense.Id, e.target.value);
                          }}
                          radius="10px 0px 0px 10px"
                        />
                        <Currencies
                          value={expense?.FxCurrency?.CurrencyName || expense?.ExpenseCurrency?.CurrencyName}
                          currencies={currencies}
                          action={(e, newValue) => {
                            setCurrency(newValue.label);
                            if (newValue.label !== expense?.ExpenseCurrency?.CurrencyName) {
                              setShowCurrency(true);
                            } else {
                              setFxRate(1);
                              setCurrency(expense?.ExpenseCurrency?.CurrencyName);
                              changeHandler(ExpenseColumnType.RATE, expense.Id, 1);
                            }
                            changeHandler(ExpenseColumnType.FX_CURRENCY, expense.Id, newValue);
                          }}
                        />
                      </>
                    )}

                    <p className={classes.expenseCurrencyValue}>= {`${expenseCurrency} ${expense?.TotalAmount}`}</p>
                  </div>
                  <NotificationModal
                    open={showCurrency}
                    onClose={() => setShowCurrency(false)}
                    paddingX={size.width > responsiveSizeTimesheet ? '36px' : '49px'}
                    height="auto"
                    gap="44px"
                  >
                    <p className={classes.expenseCurrencyText}>
                      {fxRate || expense?.FxAmount > 1
                        ? t('dashboard.createclaim.steps.currentfx')
                        : t('dashboard.createclaim.steps.currencychange')}
                    </p>
                    <div className={classes.filedsContainer}>
                      <Rate
                        t={t}
                        expense={expense}
                        action={(e) => {
                          setFxRate(e.target.value);
                          changeHandler(ExpenseColumnType.RATE, expense.Id, e.target.value);
                        }}
                        value={expense?.FxAmount || null}
                        radius="10px"
                      />
                      <span className={classes.expenseCurrencyText}>{expense?.ExpenseCurrency?.CurrencyName}</span>
                    </div>
                    <div className={classes.fullWidthandHeight}>
                      <SaveandUpdateButton t={t} size={size} action={() => setShowCurrency(false)} fxRate={fxRate} />
                    </div>
                  </NotificationModal>
                  {expense?.CategoryId !== 5 &&
                    category.value !== 5 &&
                    (currency || expense?.FxCurrency?.CurrencyName) !== expense?.ExpenseCurrency?.CurrencyName && (
                      <button onClick={() => setShowCurrency(true)} className={classes.forexRate}>
                        {t('common.general.updaterate')}
                      </button>
                    )}
                </div>
                <DayPicker
                  t={t}
                  action={(newValue) => changeHandler(ExpenseColumnType.DATE, expense.Id, newValue)}
                  classes={classes}
                  value={expense?.Date}
                />
                <Comment
                  t={t}
                  classes={classes}
                  action={(e) => changeHandler(ExpenseColumnType.COMMENT, expense.Id, e.target.value)}
                  value={expense?.Comment}
                />
                <div className={classes.uploadSection}>
                  <ReceiptUploader
                    t={t}
                    classes={classes}
                    action={(file) => uploadHandler(file, expense)}
                    qty={expense?.ExpenseAttachments?.length}
                    loading={loading}
                  />
                  <div className={classes.attachmentsList}>
                    {expense?.ExpenseAttachments?.map((attach, index) => (
                      <>
                        <AttachmentItem
                          key={attach.Id}
                          clickHandler={() => handleAttachmentViewer(attach)}
                          label={`${attach.FileAttachmentName || attach.name}`}
                          deleteHandler={() => deleteHandler(attach?.Id, expense)}
                        />
                        {index !== expense.ExpenseAttachments.length - 1 && width < responsiveSizeTimesheet && (
                          <div className={classes.divider}>
                            <div />
                          </div>
                        )}
                      </>
                    ))}
                  </div>
                </div>
                {expense?.ExpenseAttachments?.length === 0 && (
                  <div className={classes.checkboxContainer}>
                    <NoReceiptCheckbox
                      checked={checked}
                      action={(e) => handleChecked(e, expense)}
                      label={t('dashboard.createclaim.steps.noreceipt')}
                    />
                  </div>
                )}
                {(attachmentModal?.show || opening) && (
                  <CustomDocViewer
                    settings={attachmentModal}
                    handleDownload={handleDownload}
                    close={() =>
                      setAttachmentModal({
                        show: false,
                        id: '',
                        filename: '',
                        data: null
                      })
                    }
                    isLoading={opening}
                  />
                )}
                <TimeandExpenseActions
                  isDetailHandler
                  deleteHandler={() => deleteLineHandler(expense?.Id)}
                  error={error}
                  save={saveHandler}
                  submit={submitHandler}
                  approve={() => {}}
                  reject={() => {}}
                  isApprover={isApprover}
                  status={expense?.Status?.Status}
                  customTypes={[
                    {
                      label: 'delete',
                      handler: () => deleteLineHandler(expense?.Id),
                      variant: 'secondary',
                      disableHandler: false
                    },
                    {
                      label: 'save',
                      handler: () => saveHandler(expense?.Id),
                      variant: 'primary',
                      disableHandler: !ctx.hasChanges,
                      saving
                    }
                  ]}
                />
              </div>
            </ThreeDotContainer>
          ) : (
            <ThreeDotContainer>
              <div className={classes.mobileItemWrapper}>
                <div className={classes.mobileItemWrapperSection}>
                  <div className={classes.focusedText}>
                    {expense?.ExpenseCurrencyId === expense?.FxCurrencyId
                      ? `${expense?.TotalAmount} ${expense?.ExpenseCurrency?.CurrencyName}`
                      : `${expense?.ExpenseAmount} ${expense?.FxCurrency?.CurrencyName}
                       = ${expense?.TotalAmount} ${expense?.ExpenseCurrency?.CurrencyName}`}
                  </div>
                </div>
                {expense.ExpenseCurrencyId !== expense.FxCurrencyId && (
                  <div className={classes.mobileItemWrapperSection}>
                    <span className={classes.key}>{t('dashboard.expenses.table.rate')}</span>
                    <span className={classes.label}>
                      {expense.FxAmount} {expense.ExpenseCurrency.CurrencyName}
                    </span>
                  </div>
                )}

                <div className={classes.mobileItemWrapperSection}>
                  <span className={classes.key}>{t('common.general.date')}</span>
                  <span className={classes.label}>{dayjs(expense?.Date).format(generalEnums.DATEWITH_MMM)}</span>
                </div>
                <div className={classes.mobileItemWrapperSection}>
                  <span className={classes.key}>{t('common.general.category')}</span>
                  <span className={classes.label}>{expense?.Category?.Name}</span>
                </div>
                <div className={classes.mobileItemWrapperSection}>
                  {expense.ExpenseAttachments.length ? (
                    <ButtonComponent
                      variant={buttonVariants.FULL_ROUNDED}
                      classes={classes.attachCustomButton}
                      action={handleAttachments}
                      text={`${t('common.general.attachments')} (${expense?.ExpenseAttachments.length})`}
                    >
                      <span className={classes.attachmentIcon}>
                        <AddAttachmentIcon />
                      </span>
                    </ButtonComponent>
                  ) : (
                    <>
                      <span className={classes.key}>{t('dashboard.expenses.table.receipt')}</span>
                      <span className={classes.label}>{t('dashboard.expenses.table.noattachments')}</span>
                    </>
                  )}

                  <BaseModal
                    open={openPopUp}
                    fullscreen
                    closeHandler={() => setOpenPop(false)}
                    title="&nbsp;"
                    hideFooter
                    hideHeader
                    withSeparator={false}
                    marginLeftRight={1}
                    dialogContentStyle={{ overflowY: 'auto' }}
                  >
                    <WithCloseIcon sx={{ zIndex: 9, top: '-40px' }} close={() => closeAttachmentViewer(expense)}>
                      <div className={classes.docViewer}>
                        {attachmentList.length && (
                          <DocViewer pluginRenderers={DocViewerRenderers} documents={attachmentList} />
                        )}
                      </div>
                    </WithCloseIcon>
                  </BaseModal>
                </div>
                <div className={classes.mobileItemWrapperSection}>
                  {isApprover ? (
                    <h6>{t('dashboard.timesheet.table.comment')}</h6>
                  ) : (
                    <h6>
                      {`${t('dashboard.timesheet.table.messagefrom')}`}
                      <span>{`${data?.User?.FirstName} ${data?.User?.LastName}`}</span>
                    </h6>
                  )}
                  <div className={classes.boxMessage}>{expense?.Comment}</div>
                </div>
              </div>
            </ThreeDotContainer>
          )}
        </ThreeDotMenu>
      ))}
      {editable && (
        <div
          role="presentation"
          onClick={addNewClaim}
          style={{
            cursor: 'pointer',
            display: 'flex',
            flexWrap: 'nowrap',
            flexDirection: 'row',
            marginRight: 'auto',
            marginLeft: '8px',
            gap: '4px',
            color: colors.ICON_GREY
          }}
        >
          <AddGradientIcon />
        </div>
      )}
      <div className={classes.totalClaim}>
        {expenseCurrency} {totalClaim}
      </div>
    </div>
  );
}
